import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import { handleBulkUploadOfDevices } from 'api/admin/businesses/businesses';
import { ApiError } from 'types/api';

interface BulkDeviceUploadPayload {
  successCallback?: () => void;
}

interface DeviceDataPayload {
  businessId: number;
  storeId: number | null;
  devices: string[];
}

interface ValidationError {
    deviceMacAddress: string;
    errorMessage: string;
}

interface SuccessResponse {
  success: boolean;
  validationErrors: ValidationError[];
  successCount: number;
  failedCount: number;
}

export const useHandleBulkDeviceUpload = ({ successCallback }: BulkDeviceUploadPayload) => {
  return useMutation<unknown, ApiError, DeviceDataPayload>({
    mutationFn: async ({ businessId, storeId, devices }) => {
      if (!businessId || !devices || devices.length === 0 || storeId === undefined) {
        return null;
      }

      const { data } = (await handleBulkUploadOfDevices({
        businessId,
        storeId,
        devices,
      })) as AxiosResponse<SuccessResponse>;

      return data;
    },
    onSuccess: (success: SuccessResponse) => {
      successCallback?.();
      toast(`${success.successCount} device${success.successCount !== 1 ? 's have' : ' has'} been uploaded!`, {
        duration: 4000,
      });
    },
    onError: (error) => {
      Sentry.captureException(error);
      toast.error(
        error?.response?.data?.error ?? 'An error occurred while uploading your list of devices',
        {
          duration: 4000,
        },
      );
    },
  });
};
